import { Trans } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { EnvironmentBadge } from "~/components/environment-badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { PasswordInput } from "~/components/ui/password-input";
import { useAuthSignUpForm } from "~/forms/auth/sign-up";
import { cn } from "~/lib/utils";
import type { IOptionable } from "~/models/optionable";

export function SignUpForm({
	className,
	languages,
	...props
}: React.ComponentPropsWithoutRef<"div"> & { languages: Array<IOptionable> }) {
	const form = useAuthSignUpForm();

	return (
		<div className={cn("flex flex-col gap-6", className)} {...props}>
			<Card>
				<CardHeader className="text-center">
					<CardTitle className="text-xl">
						<Trans>Welcome to Podcast</Trans>
					</CardTitle>
					<CardDescription>
						<Trans>Create your account</Trans>
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form.AppForm>
						<form
							onSubmit={(e) => {
								e.preventDefault();
								e.stopPropagation();
								form.handleSubmit();
							}}
						>
							<div className="grid gap-6">
								<form.FormErrorMessages />
								<div className="grid gap-6">
									<form.AppField
										name="emailAddress"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<field.FormLabel>
													<Trans>Email</Trans>
												</field.FormLabel>
												<field.FormControl>
													<field.FormInput placeholder="podcast@azerion.com" type="email" />
												</field.FormControl>
												<field.FormMessage />
											</field.FormItem>
										)}
									/>
									<form.AppField
										name="userName"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<field.FormLabel>
													<Trans>Username</Trans>
												</field.FormLabel>
												<field.FormControl>
													<field.FormInput autoComplete="username" placeholder="john-doe" />
												</field.FormControl>
												<field.FormMessage />
											</field.FormItem>
										)}
									/>
									<form.AppField
										name="password"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<field.FormLabel>
													<Trans>Password</Trans>
												</field.FormLabel>
												<field.FormControl>
													<field.FormInput asChild>
														<PasswordInput autoComplete="new-password" />
													</field.FormInput>
												</field.FormControl>
												<field.FormMessage />
											</field.FormItem>
										)}
									/>
									<div className="grid grid-cols-12 items-start gap-4">
										<form.AppField
											name="publisherID"
											children={(field) => (
												<field.FormItem className="col-span-4 grid gap-2">
													<field.FormLabel>
														<Trans>Publisher ID</Trans>
													</field.FormLabel>
													<field.FormControl>
														<field.FormInput
														// min="0"
														// type="number"
														/>
													</field.FormControl>
													<field.FormMessage />
												</field.FormItem>
											)}
										/>
										<form.AppField
											name="languageCode"
											children={(field) => {
												return (
													<field.FormItem className="relative col-span-8 grid gap-2">
														<field.FormLabel>
															<Trans>Language</Trans>
														</field.FormLabel>
														<field.FormCombobox options={languages} />
														<field.FormMessage />
													</field.FormItem>
												);
											}}
										/>
									</div>
									<form.AppField
										name="secretCode"
										children={(field) => (
											<field.FormItem className="grid gap-2">
												<field.FormLabel>
													<Trans>Secret Code</Trans>
												</field.FormLabel>
												<field.FormControl>
													<field.FormInput asChild>
														<PasswordInput />
													</field.FormInput>
												</field.FormControl>
												<field.FormMessage />
												<field.FormDescription>
													<Trans>
														This code is required to access the platform during the closed beta.
													</Trans>
												</field.FormDescription>
											</field.FormItem>
										)}
									/>
									<form.FormButton>
										<Trans>Sign up</Trans>
									</form.FormButton>
								</div>
								<div className="text-center text-sm">
									<Trans>Already have an account?</Trans>{" "}
									<Link className="underline underline-offset-4" to="/auth/login">
										<Trans>Sign in</Trans>
									</Link>
								</div>
							</div>
						</form>
					</form.AppForm>
				</CardContent>
			</Card>
			<div className="text-muted-foreground [&_a]:hover:text-primary text-center text-xs text-balance [&_a]:underline [&_a]:underline-offset-4">
				<Trans>
					By clicking continue, you agree to our <a href="https://azerion.com">Terms of Service</a>{" "}
					and <a href="https://azerion.com">Privacy Policy</a>.
				</Trans>
			</div>
			<div className="text-center">
				<EnvironmentBadge />
			</div>
		</div>
	);
}
